import { Chip, Skeleton } from "@heroui/react";
import type { Recipe } from "../Recipes-renamed/types";
import { Link } from "react-router";
import { useGetRecipeImage } from "@/hooks/useGetRecipeImage";
import { RecipeType, recipeTypeToPath } from "@/lib/types";
import { useState } from "react";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import { formatRelativeTime } from "@/lib/relativeTime";
import { RecipeImageFallback } from "@/components/RecipeImageFallback";

interface RecipeCardProps {
    readonly recipe: Recipe;
    readonly isSlideMode?: boolean;
    readonly size?: "default" | "sm";
    readonly showAddedAgo?: boolean;
}

export const RecipeCard2 = (props: RecipeCardProps) => {
    const { recipe, isSlideMode, size = "default", showAddedAgo } = props;
    const { id, name, recipeType, image, favorite, hasMadeIt, categories, createdAt } = recipe;
    const addedAgo = showAddedAgo ? formatRelativeTime(createdAt) : null;
    const { imageUrl } = useGetRecipeImage(image);
    const [imageSrcLoaded, setImageSrcLoaded] = useState(false);

    const isSm = size === "sm";
    const firstCategory = categories?.[0];
    const extraCategoryCount = (categories?.length ?? 0) - 1;

    const imageHeight = isSm ? "h-44" : "h-110";
    const titleSize = isSm ? "text-xl" : "text-2xl";

    return (
        <div className={`group ${isSlideMode ? "max-w-[332px]" : ""}`}>
            <figure className="relative transition-shadow duration-300 ease-in-out group-hover:shadow-md shadow-primary-100 rounded-lg overflow-hidden mb-3">
                <Link to={`/recipes/${id}`} state={recipe}>
                    {imageUrl && !imageSrcLoaded && (
                        <Skeleton className="absolute inset-0 rounded-lg" />
                    )}
                    {imageUrl ? (
                        <img
                            className={`object-cover w-full ${imageHeight} max-w-full rounded-lg`}
                            src={imageUrl}
                            alt={name}
                            onLoad={() => setImageSrcLoaded(true)}
                            onError={() => setImageSrcLoaded(true)}
                        />
                    ) : (
                        <RecipeImageFallback compact className={`rounded-lg ${imageHeight} w-full`} />
                    )}
                </Link>
            </figure>
            <div className="mt-3 mb-1 flex items-center gap-2">
                <Link
                    to={`/recipes/type/${recipeTypeToPath(recipeType.name as RecipeType)}`}
                    className="font-Cinzel text-xs font-semibold uppercase tracking-[0.12em] text-[var(--accent)] hover:opacity-75 transition-opacity"
                >
                    {recipeType.name}
                </Link>
                {favorite && (
                    <Chip
                        size="sm"
                        color="accent"
                        aria-label="Favorite"
                        className="!p-0 !h-4 !w-4 !min-w-0 rounded-full inline-flex items-center justify-center -translate-y-px"
                    >
                        <StarIconSolid className="h-2.5 w-2.5" />
                    </Chip>
                )}
            </div>
            <h3 className={`font-serif ${titleSize} leading-[1.18] font-medium text-foreground ${addedAgo ? "mb-1" : "mb-3"}`}>
                <Link
                    className="underline decoration-1 underline-offset-4 decoration-transparent group-hover:decoration-primary-300 transition-all duration-300 ease-in-out"
                    to={`/recipes/${id}`}
                    state={recipe}
                >
                    {name}
                </Link>
            </h3>
            {addedAgo && (
                <p className="font-serif italic text-sm text-[var(--fg-muted)] mb-3">
                    {addedAgo}
                </p>
            )}
            {(hasMadeIt || firstCategory) && (
                <div className="flex flex-wrap items-center gap-1.5 mb-4">
                    {hasMadeIt && (
                        <Chip size="sm" className="chip--mystic">
                            <Chip.Label>Made It</Chip.Label>
                        </Chip>
                    )}
                    {firstCategory && (
                        <Chip size="sm" className="chip--slate">
                            <Chip.Label>{firstCategory.name}</Chip.Label>
                        </Chip>
                    )}
                    {extraCategoryCount > 0 && (
                        <Chip size="sm" className="chip--slate" title={categories.slice(1).map((c) => c.name).join(", ")}>
                            <Chip.Label>+{extraCategoryCount}</Chip.Label>
                        </Chip>
                    )}
                </div>
            )}
        </div>
    );
};
